import React, { useEffect, useState } from 'react';
import { TableColumn, Table, Modal, Icon, Spinner } from '@scale/scaleui';
import moment from 'moment';

interface IHistoryTableModalProps {
  fetchEvents: () => Promise<IHistoryTableEvent[]>;
}

interface IHistoryTableEvent {
  user: string;
  operation: string;
  timestamp: Date;
  changes: { [key: string]: any };
}

const StaticColumns: TableColumn[] = [
  {
    id: 'timestamp',
    header: 'Timestamp',
    enableSorting: true,
    cell: function FormatDate(props: any) {
      return moment(props.getValue()).tz('America/Los_Angeles').format('MMM D YYYY, h:mm A (z)');
    },
  },
  {
    id: 'user',
    header: 'User',
    enableSorting: true,
  },
  {
    id: 'operation',
    header: 'Operation',
    enableSorting: true,
  },
];

const generateDynamicColumns = (events: IHistoryTableEvent[]): TableColumn[] => {
  const uniqueKeys = new Set<string>();
  const maxCharacters = new Map<string, number>();

  events.forEach(event => {
    if (event.changes) {
      Object.keys(event.changes).forEach(key => {
        uniqueKeys.add(key);
        if (event.changes[key]) {
          const stringified = JSON.stringify(event.changes[key]);
          maxCharacters.set(key, Math.max(maxCharacters.get(key) || 0, stringified.length));
        }
      });
    }
  });

  return Array.from(uniqueKeys).map(key => ({
    id: 'changes.' + key,
    header: key,
    enableSorting: false,
    cellClassName: maxCharacters.get(key)! >= 75 ? 'whitespace-pre-wrap' : '',
    size: maxCharacters.get(key)! >= 75 ? 600 : 0,
    cell: function FormatJson(props: any) {
      return props.getValue() ? JSON.stringify(props.getValue(), null, 4) : '';
    },
  }));
};

export const HistoryTableModal: React.FC<IHistoryTableModalProps> = ({
  fetchEvents,
}: IHistoryTableModalProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [events, setEvents] = useState<IHistoryTableEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      const fetchedEvents = await fetchEvents();
      setEvents(fetchedEvents);
      setLoading(false);
    };

    if (isOpen) {
      loadEvents();
    }
  }, [isOpen, fetchEvents]);

  const dynamicColumns = generateDynamicColumns(events);
  const columns = [...StaticColumns, ...dynamicColumns];

  return (
    <>
      <Icon
        icon={['fas', 'square-list']}
        size="sm"
        tooltip="History"
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
      />

      <Modal open={isOpen} onClose={() => setIsOpen(false)} title="History">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Spinner size="lg" />
          </div>
        ) : (
          <Table columns={columns} data={events} defaultPageSize={10} />
        )}
      </Modal>
    </>
  );
};
